import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  customerCheckBill,
  type CustomerCheckBillResponse,
} from "../../services/api.service";
import "./CheckBill.css";

type FieldErrors = {
  customerCode?: string;
  identificationValue?: string;
};

type Touched = {
  customerCode: boolean;
  identificationValue: boolean;
};

function validateDigitsField(
  value: string,
  requiredMessage: string,
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage;
  if (!/^\d+$/.test(trimmed)) return "יש להזין ספרות בלבד";
  if (trimmed.length < 3) return "מספר לא תקין - קצר מידי";
  if (trimmed.length > 20) return "מספר לא תקין - ארוך מידי";
  return undefined;
}

export type IdentificationMode = "gas_account" | "gas_account_id" | "id_mobile";
export type IdentificationType = "invoice" | "id" | "phone";

export type CheckBillSuccessPayload = {
  customerCode: string;
  identificationType: IdentificationType;
  identificationValue: string;
  firmCode?: 3;
};

export function CheckBill({
  mode,
  onSuccess,
}: {
  mode: IdentificationMode;
  onSuccess: (payload: CheckBillSuccessPayload) => void;
}) {
  const [customerCode, setCustomerCode] = useState("");
  const [identificationValue, setIdentificationValue] = useState("");
  const [touched, setTouched] = useState<Touched>({
    customerCode: false,
    identificationValue: false,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    severity: "warning" | "error";
    message: string;
  }>({ open: false, severity: "warning", message: "" });

  const modeConfig = useMemo(() => {
    if (mode === "gas_account") {
      return {
        identificationType: "invoice" as const,
        subtitle: "הזן את פרטי החשבון כפי שמופיעים בחשבונית הגז",
        secondLabel: "*מספר חשבונית",
        secondRequiredMessage: "יש להזין מספר חשבונית",
        showExample: true,
      };
    }
    if (mode === "gas_account_id") {
      return {
        identificationType: "id" as const,
        subtitle: "הזן מספר צרכן ומספר ת.ז של בעל החשבון",
        secondLabel: "*מספר תז",
        secondRequiredMessage: "יש להזין מספר תז",
        showExample: false,
      };
    }
    return {
      identificationType: "phone" as const,
      subtitle: "הזן מספר צרכן ומספר טלפון נייד של בעל החשבון",
      secondLabel: "*מספר טלפון נייד",
      secondRequiredMessage: "יש להזין מספר טלפון נייד",
      showExample: false,
    };
  }, [mode]);

  useEffect(() => {
    setCustomerCode("");
    setIdentificationValue("");
    setTouched({ customerCode: false, identificationValue: false });
    setErrors({});
    setSnack((s) => ({ ...s, open: false }));
  }, [mode]);

  const currentErrors = useMemo((): FieldErrors => {
    return {
      customerCode: validateDigitsField(customerCode, "יש להזין מספר צרכן"),
      identificationValue: validateDigitsField(
        identificationValue,
        modeConfig.secondRequiredMessage,
      ),
    };
  }, [customerCode, identificationValue, modeConfig.secondRequiredMessage]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ customerCode: true, identificationValue: true });
    setErrors(currentErrors);

    if (currentErrors.customerCode || currentErrors.identificationValue) return;

    const trimmedCustomerCode = customerCode.trim();
    const trimmedIdentificationValue = identificationValue.trim();

    try {
      setLoading(true);
      setSnack((s) => ({ ...s, open: false }));

      let validationResult: CustomerCheckBillResponse = undefined;

      if (modeConfig.identificationType === "invoice") {
        validationResult = await customerCheckBill({
          customerCode: trimmedCustomerCode,
          identificationType: "invoice",
          invoiceNumber: trimmedIdentificationValue,
        });
      } else if (modeConfig.identificationType === "id") {
        validationResult = await customerCheckBill({
          customerCode: trimmedCustomerCode,
          identificationType: "id",
          personalIdNumber: trimmedIdentificationValue,
        });
      } else {
        validationResult = await customerCheckBill({
          customerCode: trimmedCustomerCode,
          identificationType: "phone",
          mobilePhoneNumber: trimmedIdentificationValue,
        });
      }

      onSuccess({
        customerCode: trimmedCustomerCode,
        identificationType: modeConfig.identificationType,
        identificationValue: trimmedIdentificationValue,
        firmCode: validationResult === 3 ? 3 : undefined,
      });
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;

      if (status === 404) {
        setSnack({
          open: true,
          severity: "warning",
          message:
            modeConfig.identificationType === "invoice"
              ? "לא הצלחנו לזהות אותך לפי הפרטים שהוזנו. נסה שוב או בחר הזדהות עם תז או טלפון."
              : "לא הצלחנו לזהות אותך לפי הפרטים שהוזנו. נסה שוב.",
        });
        return;
      }

      setSnack({
        open: true,
        severity: "error",
        message: "אירעה שגיאה. נסה שוב מאוחר יותר.",
      });
    } finally {
      setLoading(false);
    }
  }

  function showError(field: keyof FieldErrors) {
    if (!touched[field]) return undefined;
    return (errors[field] ?? currentErrors[field]) || undefined;
  }

  return (
    <Paper
      elevation={0}
      dir="rtl"
      sx={{
        pb: 3,
        pl: 3,
        pr: 3,
        pt: 0,
        borderRadius: 1,
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: 1280,
      }}
    >
      <Typography color="text.secondary" sx={{ mb: 1, fontSize: 19 }}>
        {modeConfig.subtitle}
      </Typography>

      <div className="checkBillColumns">
        <div className="checkBillFormCol" style={{ maxWidth: 380 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              sx={{ mb: 0 }}
              fullWidth
              label="*מספר צרכן"
              value={customerCode}
              onChange={(e) => setCustomerCode(e.target.value)}
              onBlur={() => {
                setTouched((t) => ({ ...t, customerCode: true }));
                setErrors((prev) => ({
                  ...prev,
                  customerCode: currentErrors.customerCode,
                }));
              }}
              error={Boolean(showError("customerCode"))}
              helperText={showError("customerCode") || " "}
              margin="normal"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />

            <TextField
              sx={{ mt: 0 }}
              fullWidth
              label={modeConfig.secondLabel}
              value={identificationValue}
              onChange={(e) => setIdentificationValue(e.target.value)}
              onBlur={() => {
                setTouched((t) => ({ ...t, identificationValue: true }));
                setErrors((prev) => ({
                  ...prev,
                  identificationValue: currentErrors.identificationValue,
                }));
              }}
              error={Boolean(showError("identificationValue"))}
              helperText={showError("identificationValue") || " "}
              margin="normal"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 1,
                minHeight: 48,
                fontWeight: 800,
                borderRadius: 1,
                fontSize: 20,
              }}
            >
              {loading ? (
                <CircularProgress size={34} sx={{ color: "black" }} />
              ) : (
                "המשך"
              )}
            </Button>
          </Box>

          {snack.open && (
            <Alert
              severity={snack.severity}
              icon={false}
              sx={{
                width: "100%",
                mt: 2,
                fontWeight: 500,
                fontSize: 17,
                textAlign: "center",
              }}
            >
              {snack.message}
            </Alert>
          )}
        </div>

        <div className="checkBillExampleCol">
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: "background.default",
            }}
          >
            <Typography fontWeight={800} sx={{ mb: 1, fontSize: 19 }}>
              חשבונית לדוגמא
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontSize: 17 }}
            >
              היעזרו בתמונה כדי לאתר את מספר הצרכן ומספר החשבונית.
            </Typography>
            <div className="checkBillExampleImageWrap">
              <Box
                component="img"
                src="https://epstorageprod01.blob.core.windows.net/selfservice/sgp-bill-2025.png"
                alt="חשבונית לדוגמא"
                loading="lazy"
                sx={{ width: "100%", height: "auto", display: "block" }}
              />
            </div>
          </Paper>
        </div>
      </div>
    </Paper>
  );
}
